import React, { useState, useEffect, useCallback } from "react";
import { Row, Label } from "reactstrap";

import { CustomModal } from "../../components/CustomModal";
import { AutoComplete } from "../../components/AutoComplete";
import { GetAudiosSubCategories } from "../../api/api.service";
import { customStyles } from "../UserManagement/CustomStyles";
import { useRequestProcessor } from "../../hooks/useRequestProcessors";
import { getKey, MODULE_KEYS } from "../../api/api.keys";
import { CustomButton } from "../../components/CustomButton";
import { STATUS_OPTIONS, subsCriptionfilterOptions } from "./Config";

const AudioFilterModal = ({ isModalOpen, handleClose, handleApplyFilter, selectedFilters }) => {
    const [localFilters, setLocalFilters] = useState(selectedFilters);
    const [options, setOptions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const { useFetch } = useRequestProcessor();

    const { data, isLoading, isFetching } = useFetch(
        getKey([MODULE_KEYS.AUDIOS_SUB_CATEGORY, currentPage], {}),
        async ({ signal }) =>
            await GetAudiosSubCategories({
                params: { page: currentPage, limit: 10 },
                signal
            }),
        {
            keepPreviousData: true,
            enabled: hasMore
        }
    );

    useEffect(() => {
        setLocalFilters(selectedFilters); // Sync local state when filters change
    }, [selectedFilters]);

    useEffect(() => {
        if (data?.data) {
            const newOptions = data?.data?.map((item) => ({
                value: item.id,
                label: item.title
            }));

            setOptions((prevOptions) => [...prevOptions, ...newOptions]);
            if (options.length + newOptions.length >= data.total) {
                setHasMore(false);
            }
        }
    }, [data]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 20 && hasMore && !isFetching) {
            setCurrentPage((prevPage) => prevPage + 1);
        }
    };

    const handleChange = (key, value) => {
        setLocalFilters((prev) => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSubmit = () => {
        handleApplyFilter(localFilters);
        handleClose(false);
    };

    const renderAutoComplete = useCallback(
        (label, placeholder, key, options, isLoading) => (
            <Row className="my-2">
                <Label className="mb-1 fw-bold">{label}</Label>
                <AutoComplete
                    handleScroll={key === "subCategory" ? handleScroll : null}
                    customStyles={customStyles.filter}
                    options={options}
                    onChange={(value) => handleChange(key, value)}
                    value={localFilters[key]}
                    placeholder={placeholder}
                    isClearable
                    isMulti={key === "subCategory" ? true : false}
                    maxMenuHeight={220}
                    isLoading={isLoading}
                    classNamePrefix="status-header-search-admin"
                    theme={(theme) => ({
                        ...theme,
                        colors: {
                            ...theme.colors,
                            primary: "#2795ae"
                        }
                    })}
                />
            </Row>
        ),
        [localFilters, handleScroll]
    );

    return (
        <CustomModal
            isOpen={isModalOpen}
            size="md"
            className="filter-modal"
            modalBodyClasses="d-flex justify-content-center"
            customButton
            modalTitle={<h2 className="m-0 px-2">Audio Filter</h2>}
            handleModalToggling={() => handleClose(false)}
        >
            <div>
                {renderAutoComplete(
                    "Audio Subcategories",
                    "Select  sub-category ",
                    "subCategory",
                    options,
                    isLoading || isFetching
                )}
                {renderAutoComplete("Status", "Select status", "status", STATUS_OPTIONS, false)}
                {renderAutoComplete(
                    "Audio Type",
                    "Select audio type",
                    "audioType",
                    subsCriptionfilterOptions,
                    false
                )}
                <div className="d-flex justify-content-evenly w-100 my-2 mt-4">
                    <CustomButton
                        title="Cancel"
                        className="me-3 custom-btn-sm"
                        color="primary"
                        outline
                        onClick={() => handleClose(false)}
                    />
                    <CustomButton
                        title="Apply"
                        color="primary"
                        className="shadow-theme grad-primary"
                        onClick={handleSubmit}
                    />
                </div>
            </div>
        </CustomModal>
    );
};

export default AudioFilterModal;
